import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft, Check, Clock, AlertTriangle, User, FileText,
    Ticket as TicketIcon, ShieldCheck, X,
} from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet, apiPatch } from "@/lib/api";
import { ORDER_STATUS_VARIANT, PRIORITY_VARIANT } from "@/lib/rbac";
import { slaInfo, formatAOAFull, formatRel } from "@/lib/sla";
import pt from "@/lib/i18n";

const STATUS_LABEL = (s) => pt.services[`status_${s}`] || s;
const STEP_LABEL = (s) => pt.services[`step_${s}`] || s;

const ACTIONS_BY_STATUS = {
    pending_docs: [{ to: "pending_approval", label: pt.services.submitForApproval || "Submeter para aprovação", variant: "copper" }],
    pending_approval: [
        { to: "approved", label: pt.services.approve, variant: "copper" },
        { to: "rejected", label: pt.services.reject, variant: "danger", requiresReason: true },
    ],
    approved: [{ to: "in_provisioning", label: pt.services.startProvisioning, variant: "copper" }],
    in_provisioning: [{ to: "active", label: pt.services.activate, variant: "copper" }],
    draft: [{ to: "pending_docs", label: "Iniciar", variant: "copper" }],
};

const OrderDetailPage = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [showReject, setShowReject] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const d = await apiGet(`/service-orders/${orderId}`);
            setData(d);
        } catch (e) {
            setData(null);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    const transition = async (to, reason) => {
        setBusy(true);
        try {
            await apiPatch(`/service-orders/${orderId}/status`, {
                status: to,
                rejection_reason: reason || null,
            });
            await load();
            setShowReject(false);
            setRejectReason("");
        } catch (e) { /* ignore */ }
        finally { setBusy(false); }
    };

    if (!data) {
        return (
            <div>
                <Skeleton height="40px" width="320px" />
                <div className="mt-6">
                    <Skeleton height="200px" />
                </div>
            </div>
        );
    }

    const { order, customer, service, workflow_steps, documents, tickets, assignee } = data;
    const sla = slaInfo(order.sla_deadline);
    const actions = ACTIONS_BY_STATUS[order.status] || [];

    return (
        <div data-testid="order-detail-root">
            <button
                type="button"
                onClick={() => navigate("/servicos")}
                className="inline-flex items-center gap-1.5 text-sm text-[color:var(--slate-mid)] hover:text-[color:var(--copper-dark)] mb-4"
                data-testid="back-to-services"
            >
                <ArrowLeft size={14} /> {pt.services.backToList}
            </button>

            <PageHeader
                title={<span className="font-mono">{order.order_number}</span>}
                subtitle={`${customer?.full_name || "—"} · ${service?.name || "—"}`}
                right={
                    <div className="flex items-center gap-2">
                        <NxBadge variant={ORDER_STATUS_VARIANT[order.status]}>{STATUS_LABEL(order.status)}</NxBadge>
                        <NxBadge variant={PRIORITY_VARIANT[order.priority]}>{pt.services[`priority_${order.priority}`]}</NxBadge>
                        <NxBadge variant={sla.variant}><Clock size={12} /> {sla.label}</NxBadge>
                    </div>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left: workflow timeline */}
                <GlassCard padding="p-6" className="lg:col-span-2" testId="workflow-timeline">
                    <h3 className="font-display text-xl text-[color:var(--slate-deep)] mb-4">{pt.services.workflow}</h3>
                    <ol className="space-y-3 relative">
                        {workflow_steps.map((s, idx) => {
                            const completed = s.status === "completed";
                            const inProgress = s.status === "in_progress";
                            return (
                                <li key={s.id} className="flex gap-4" data-testid={`step-${s.step_number}`}>
                                    <div className="flex flex-col items-center">
                                        <div
                                            className={
                                                "h-9 w-9 rounded-full grid place-items-center text-sm font-semibold flex-shrink-0 " +
                                                (completed
                                                    ? "bg-[color:var(--success)] text-white"
                                                    : inProgress
                                                      ? "bg-[color:var(--copper)] text-white shadow-[0_0_0_4px_var(--copper-glow)]"
                                                      : "bg-[color:var(--bg-surface)] text-[color:var(--slate-mid)] border border-[color:var(--glass-border)]")
                                            }
                                        >
                                            {completed ? <Check size={14} /> : s.step_number}
                                        </div>
                                        {idx < workflow_steps.length - 1 && (
                                            <div className="w-px flex-1 my-1.5 bg-[color:var(--glass-border)]" />
                                        )}
                                    </div>
                                    <div className="pb-4 flex-1">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="font-body font-medium text-[color:var(--slate-deep)]">{s.step_name}</p>
                                            <NxBadge variant={completed ? "success" : inProgress ? "warning" : "neutral"}>
                                                {STEP_LABEL(s.status)}
                                            </NxBadge>
                                        </div>
                                        {s.completed_at && (
                                            <p className="font-mono text-xs text-[color:var(--slate-light)] mt-1">
                                                Concluída {formatRel(s.completed_at)}
                                            </p>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </GlassCard>

                {/* Right: meta + actions */}
                <div className="space-y-4">
                    <GlassCard padding="p-5">
                        <h4 className="font-display text-base text-[color:var(--slate-deep)] mb-3">Detalhes</h4>
                        <dl className="space-y-2 text-sm">
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">Cliente</dt><dd className="text-[color:var(--slate-deep)]">{customer?.full_name}</dd></div>
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">{pt.common.service}</dt><dd className="text-[color:var(--slate-deep)] text-right">{service?.name}</dd></div>
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">Mensalidade</dt><dd className="font-mono text-[color:var(--copper-dark)]">AOA {formatAOAFull(service?.price_aoa)}</dd></div>
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">SLA</dt><dd className="font-mono text-[color:var(--slate-deep)]">{service?.sla_activation_hours}h</dd></div>
                            {assignee && (
                                <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">{pt.common.assignedTo}</dt><dd className="text-[color:var(--slate-deep)]">{assignee.full_name}</dd></div>
                            )}
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">Criada</dt><dd className="font-mono text-xs text-[color:var(--slate-mid)]">{formatRel(order.created_at)}</dd></div>
                        </dl>
                    </GlassCard>

                    {actions.length > 0 && (
                        <GlassCard padding="p-5">
                            <h4 className="font-display text-base text-[color:var(--slate-deep)] mb-3">{pt.common.actions}</h4>
                            <div className="flex flex-col gap-2">
                                {actions.map((a) => (
                                    <NxButton
                                        key={a.to}
                                        variant={a.variant}
                                        loading={busy}
                                        onClick={() => (a.requiresReason ? setShowReject(true) : transition(a.to))}
                                        testId={`action-${a.to}`}
                                    >
                                        {a.label}
                                    </NxButton>
                                ))}
                            </div>
                        </GlassCard>
                    )}

                    {order.rejection_reason && (
                        <GlassCard padding="p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle size={16} className="text-[color:var(--error)]" />
                                <h4 className="font-display text-base text-[color:var(--error)]">{pt.common.rejectionReason}</h4>
                            </div>
                            <p className="text-sm text-[color:var(--slate-mid)]">{order.rejection_reason}</p>
                        </GlassCard>
                    )}
                </div>

                {/* Documents */}
                <GlassCard padding="p-6" className="lg:col-span-2" testId="documents-panel">
                    <h3 className="font-display text-xl text-[color:var(--slate-deep)] mb-3">{pt.services.documentsPanel}</h3>
                    {documents.length === 0 ? (
                        <p className="text-sm text-[color:var(--slate-mid)]">Nenhum documento carregado.</p>
                    ) : (
                        <ul className="space-y-2">
                            {documents.map((d) => (
                                <li key={d.id} className="rounded-xl p-3 border border-[color:var(--glass-border-subtle)] flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-md bg-[color:var(--copper-subtle)] grid place-items-center">
                                            <FileText size={15} className="text-[color:var(--copper-dark)]" />
                                        </div>
                                        <div>
                                            <p className="font-body text-sm font-medium text-[color:var(--slate-deep)]">{d.file_name}</p>
                                            <p className="font-mono text-xs text-[color:var(--slate-mid)]">
                                                {(d.file_size / 1024).toFixed(1)} KB · {d.document_type}
                                            </p>
                                        </div>
                                    </div>
                                    {d.is_verified ? (
                                        <NxBadge variant="success"><ShieldCheck size={12} /> Verificado</NxBadge>
                                    ) : (
                                        <NxBadge variant="warning">{pt.common.waiting}</NxBadge>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </GlassCard>

                {/* Linked tickets */}
                <GlassCard padding="p-6">
                    <h3 className="font-display text-xl text-[color:var(--slate-deep)] mb-3">{pt.services.linkedTickets}</h3>
                    {tickets.length === 0 ? (
                        <p className="text-sm text-[color:var(--slate-mid)]">{pt.common.noLinkedTickets}</p>
                    ) : (
                        <ul className="space-y-2">
                            {tickets.map((t) => (
                                <li
                                    key={t.id}
                                    className="rounded-xl p-3 border border-[color:var(--glass-border-subtle)] cursor-pointer hover:bg-[color:var(--copper-subtle)]"
                                    onClick={() => navigate(`/tickets/${t.id}`)}
                                >
                                    <p className="font-mono text-xs text-[color:var(--copper-dark)]">{t.ticket_number}</p>
                                    <p className="font-body text-sm text-[color:var(--slate-deep)] mt-1">{t.subject}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </GlassCard>
            </div>

            {/* Reject modal */}
            {showReject && (
                <div className="fixed inset-0 bg-black/30 z-50 grid place-items-center p-4" onClick={() => setShowReject(false)}>
                    <div className="glass-elevated p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display text-xl text-[color:var(--slate-deep)]">{pt.common.rejectOrder}</h3>
                            <button type="button" onClick={() => setShowReject(false)}><X size={16} /></button>
                        </div>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder={pt.services.rejectReasonPlaceholder || "Motivo da rejeição…"}
                            rows={4}
                            className="glass-input w-full"
                            data-testid="reject-reason-input"
                        />
                        <div className="flex justify-end gap-2 mt-3">
                            <NxButton variant="secondary" onClick={() => setShowReject(false)}>{pt.common.cancel}</NxButton>
                            <NxButton
                                variant="danger"
                                loading={busy}
                                onClick={() => transition("rejected", rejectReason)}
                                disabled={!rejectReason.trim()}
                                testId="confirm-reject"
                            >
                                Confirmar Rejeição
                            </NxButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderDetailPage;
